// public/v3/src/lib/ai-suggest.js — rule-based program suggestion.
// Pure functions — no LLM call. Mirrors the practice's enrollment criteria.
//
// Module format: CommonJS (module.exports). Jest reads this file directly
// (no babel-jest preset-env installed), and esbuild interops CommonJS
// modules cleanly when consumed via `import { x } from './ai-suggest'`
// from JSX pages — same pattern as lib/workflow.js and lib/panel-review.js.

function conditionLabel(c) {
  if (typeof c === 'string') return c.trim();
  if (c && typeof c === 'object') {
    // Elation patient.conditions entries carry `description` + `icd10` +
    // `conditionId`. v2's ccmChronicConditionCount (ccm-compliance.js:32-37)
    // reads `c.description || c.conditionId`. Keep the old (label/name/code)
    // keys as fallbacks for non-Elation/synthetic shapes.
    // String() guard: Elation `conditionId`/`code` are numeric, so an entry
    // with no text description would otherwise return a number and throw on
    // .trim() (mirrors conditionText() in bhi-suggest.js).
    return String(c.description || c.label || c.name || c.code || c.conditionId || '').trim();
  }
  return '';
}

function chronicLabels(patient) {
  const raw = patient?._raw || patient || {};
  const dx = Array.isArray(raw.conditions) ? raw.conditions : [];
  // The clinician-maintained problem list IS the chronic-condition list for
  // CCM purposes — every non-empty entry counts. Mirrors v2's
  // ccmChronicConditionCount (public/ccm-compliance.js:32-37). An earlier
  // CHRONIC_CONDITIONS whitelist over-filtered: it only matched curated
  // abbreviations like 'T2DM' / 'HTN', so real Elation descriptions like
  // 'DM2 (diabetes mellitus, type 2)' were silently dropped → every patient
  // appeared to have zero chronic conditions.
  return dx.map(conditionLabel).filter(label => label.length > 0);
}

// chronicEntries — like chronicLabels but preserves the ICD-10 alongside the
// label so downstream severity/neuro classifiers can match on either signal.
// Real Elation conditions carry e.g. { description: 'Chronic kidney disease,
// stage 4', icd10: 'N18.4' } — the description is the human-readable variant
// of whatever ICD-10 the clinician picked, but only the ICD-10 is structured.
function chronicEntries(patient) {
  const raw = patient?._raw || patient || {};
  const dx = Array.isArray(raw.conditions) ? raw.conditions : [];
  return dx
    .map((c) => ({
      label: conditionLabel(c),
      icd10: (c && typeof c === 'object' && c.icd10) ? String(c.icd10).toUpperCase().trim() : '',
    }))
    .filter(e => e.label.length > 0 || e.icd10.length > 0);
}

function chronicCount(patient) {
  return chronicLabels(patient).length;
}

// normLabel — lowercase, strip apostrophes (so "Parkinson's" matches
// "Parkinson disease"), and collapse whitespace. The substring regexes below
// rely on this normalization so the patterns can be authored as plain
// lowercase-no-apostrophe forms.
function normLabel(s) {
  return String(s || '').toLowerCase()
    .replace(/['‘’]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// Severe — stage-4 CKD or worse. ICD-10 N18.4/N18.5/N18.6 (plus the
// stage-specific N18.30/.31/.32 family) are the stages eligible; we
// include N18.4+. Label patterns recognize "CKD-4", "CKD stage 4",
// "Chronic kidney disease, stage 4", "ESRD", "end-stage renal disease".
// Bias is intentionally narrow — when in doubt prefer NOT to flag severe
// so the heuristic under-claims rather than over-claims Complex CCM.
function isSevereEntry(entry) {
  const icd10 = entry && entry.icd10 ? entry.icd10 : '';
  const l = normLabel(entry && entry.label);
  if (icd10) {
    if (/^N18\.(4|5|6|30|31|32)/.test(icd10)) return true;
  }
  if (!l) return false;
  if (/\bckd[-\s]*(?:stage[-\s]*)?(?:4|iv|5|v)\b/.test(l)) return true;
  if (/\bchronic\s+kidney\s+disease[, ]+stage\s+(?:4|iv|5|v)\b/.test(l)) return true;
  if (/\besrd\b/.test(l)) return true;
  if (/\bend[-\s]*stage\s+renal\s+disease/.test(l)) return true;
  return false;
}

// Neuro decline — dementia (any cause), Parkinson disease and variants,
// Lewy body, Huntington, ALS. ICD-10: F00-F03 dementia family; G20-G21
// Parkinson family; G31.83 Lewy body; G10 Huntington; G12.2 ALS family.
// Real Elation descriptions like "Alzheimer dementia, late onset" and
// "Parkinson disease, idiopathic" miss the old exact-token Set — the
// substring regex catches them.
function isNeuroDeclineEntry(entry) {
  const icd10 = entry && entry.icd10 ? entry.icd10 : '';
  const l = normLabel(entry && entry.label);
  if (icd10) {
    if (/^F0[0-3]/.test(icd10)) return true;
    if (/^G2[01]/.test(icd10)) return true;
    if (/^G31\.83/.test(icd10)) return true;
    if (/^G10/.test(icd10)) return true;
    if (/^G12\.2/.test(icd10)) return true;
  }
  if (!l) return false;
  if (/\bdementia\b/.test(l)) return true;
  if (/\balzheimer/.test(l)) return true;
  if (/\bparkinson/.test(l)) return true;
  if (/\blewy\s+body\b/.test(l)) return true;
  if (/\bhuntington\b/.test(l)) return true;
  if (/\b(?:als|amyotrophic\s+lateral\s+sclerosis)\b/.test(l)) return true;
  return false;
}

// Returns: { program, label, reason }
//
// APCM is never returned from this heuristic — it's an end-of-month per-patient
// billing-tier fallback that lives on the dedicated 3 · APCM Fallback tab. The
// heuristic picks between standard 99491 (physician-led, 30-min target — this
// practice's default tier), Complex 99487, or "don't enroll" based on
// chronic count + complexity flags. Plain 99490 (staff-led, 20-min) stays
// available via the toolbar's bulk Enroll button for the less-common case
// where the operator wants to enroll a batch into staff-led CCM.
function suggestProgram(patient) {
  const entries = chronicEntries(patient);
  const n = entries.length;

  if (n >= 2) {
    const flags = [];
    if (n >= 4) flags.push(`${n} chronic conditions`);
    const severe = entries.filter(isSevereEntry).map(e => e.label || e.icd10);
    if (severe.length) flags.push(severe.join(', '));
    const neuro = entries.filter(isNeuroDeclineEntry).map(e => e.label || e.icd10);
    // Neuro decline counts as a complexity flag when paired with ≥1 other
    // chronic — otherwise an isolated Dementia dx with nothing else doesn't
    // automatically clear the moderate-to-high MDM bar.
    if (neuro.length && n - neuro.length >= 1) flags.push(`${neuro.join('/')} + comorbidity`);

    if (flags.length) {
      return {
        program: '99487',
        label: '99487 Complex',
        reason: `${flags.join('; ')} — complex CCM threshold`,
      };
    }
    return {
      program: '99491',
      label: 'CCM 99491',
      reason: `${n} chronic conditions — meets CCM threshold (physician-led)`,
    };
  }
  if (n === 1) {
    return {
      program: 'none',
      label: 'No CCM suggestion',
      reason: '1 chronic condition — below CCM threshold',
    };
  }
  return {
    program: 'none',
    label: "Don't enroll",
    reason: 'No qualifying chronic conditions',
  };
}

function needsTriage(patient) {
  const raw = patient?._raw || patient || {};
  const program = raw.billing?.program;
  const consent = raw.consent;
  // Declined patients are out of the workflow entirely — they must never
  // surface in Triage. Without this gate, a declined patient with a missing
  // program would set `programMissing = true` and the Accept button would
  // happily write a program while leaving consent as 'declined', re-enrolling
  // someone who explicitly opted out. v2 surfaces re-consent flows via /v2;
  // v3 keeps declined out of Studio entirely.
  if (consent === 'declined') {
    return { needsProgram: false, needsConsent: false, any: false };
  }
  const programMissing = !program || program === 'none';
  const consentMissing = consent !== 'signed' && consent !== 'declined';
  return { needsProgram: programMissing, needsConsent: consentMissing, any: programMissing || consentMissing };
}

module.exports = {
  chronicCount,
  suggestProgram,
  needsTriage,
};
